/**
 * @format
 */

import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { StyleSheet, FlatList, View, Text, Platform } from 'react-native';
import lodash, { isEmpty } from 'lodash';

const debug = require('debug')('se:advert-category-list');

import {
  Advertisement,
  ThumbtackPro,
  Provider,
  isThumbtackPro,
  isAdvertisement,
  isThumbtackCategory,
  ThumbtackHomecare,
  // ThumbtackCategoriesObject,
} from '../../api/adserver';
import Divider from '../../components/Divider';
import config from '../../lib/config';
import { trackAdvertView } from '../../lib/analytics';

import CategoryAdvertListItem from './CategoryAdvertListItem';
import GridList from './GridList';
import { ThumbtackAdvertListItemProps } from './CustomAdvertListItems/ThumbtackAdvertListItem';
import ThumbtackCategoryListItem from './CustomAdvertListItems/ThumbtackCategoryListItem';

export interface Props {
  advertCategoryList?: Advertisement[];
  onPress: (
    item: Advertisement | ThumbtackPro | ThumbtackHomecare,
    // | ThumbtackCategoriesObject,
  ) => void;
  provider: Provider;
  customListItemComponent?: React.FC<ThumbtackAdvertListItemProps>;
  customListItemData: ThumbtackPro[];
  thumbtackCategoryList: ThumbtackHomecare[];
  // | ThumbtackCategoriesObject[];
}

const windowSize = 10;

const CategoryAdvertList = forwardRef<any, any>((props: Props, ref) => {
  const flatListOptimizationProps = {
    initialNumToRender: 0,
    maxToRenderPerBatch: 1,
    removeClippedSubviews: true,
    windowSize,
    keyExtractor: useCallback((e) => e.id.toString(), []),
    showsHorizontalScrollIndicator: true,
    bounces: true,
  };
  const flatListRef = useRef<FlatList>(null);
  const {
    advertCategoryList,
    onPress,
    customListItemComponent,
    provider,
    customListItemData,
    thumbtackCategoryList,
  } = props;

  const [data, setData] = useState<
    Advertisement[] | ThumbtackPro[] | ThumbtackHomecare[]
    // | ThumbtackCategoriesObject[]
  >([]);

  useEffect(() => {
    switch (provider) {
      case Provider.Thumbtack: {
        setData(customListItemData);
        break;
      }
      case Provider.ThumbtackCategory: {
        setData(thumbtackCategoryList);
        break;
      }
      case Provider.Zonetail: {
        if (advertCategoryList) {
          setData(advertCategoryList);
        }
        break;
      }
      default: {
        setData([]);
      }
    }
  }, [provider, advertCategoryList, customListItemData, thumbtackCategoryList]);

  const renderItem = useCallback(
    ({
      item,
    }: {
      item:
        | Advertisement
        | ThumbtackPro
        // | ThumbtackCategoriesObject
        | ThumbtackHomecare;
    }) => {
      const renderFeatureLabel = (
        itemFeature:
          | Advertisement
          | ThumbtackPro
          // | ThumbtackCategoriesObject
          | ThumbtackHomecare,
      ) => {
        let text = 'Featured';
        if (isThumbtackPro(itemFeature)) {
          if (itemFeature.rating < 4.9) {
            return null;
          }
        } else if (isAdvertisement(itemFeature)) {
          if (!itemFeature.featured) {
            return null;
          }
          if (itemFeature.feature_type) {
            text = itemFeature.feature_type.text;
          }
        } else if (isThumbtackCategory(itemFeature)) {
          return null;
        }
        return (
          <View
            style={[
              styles.featuredHeadingContainer,
              isAdvertisement(itemFeature)
                ? styles.featuredHeadingContainerZonetail
                : styles.featuredHeadingContainerThumbtack,
            ]}
          >
            <Text style={styles.featuredHeading}>{text}</Text>
          </View>
        );
      };
      const ListItem = () => {
        if (isThumbtackPro(item) && customListItemComponent) {
          const ThumbtackItem = customListItemComponent;
          return (
            <ThumbtackItem professional={item} onPress={onPress} margin={0} />
          );
        } else if (isThumbtackCategory(item) && thumbtackCategoryList) {
          return (
            <ThumbtackCategoryListItem
              category={item}
              onPress={onPress}
              margin={0}
            />
          );
        } else if (isAdvertisement(item)) {
          return (
            <CategoryAdvertListItem
              advert={item}
              onPress={onPress}
              margin={0}
            />
          );
        } else {
          return null;
        }
      };
      return (
        <View style={styles.flexOne}>
          <View style={styles.listItemContainer}>{ListItem()}</View>
          {renderFeatureLabel(item)}
          <Divider containerStyle={styles.divider} />
        </View>
      );
    },
    [onPress, customListItemComponent, thumbtackCategoryList],
  );

  const handleViewableItemsChanged = useCallback(
    ({ changed }: { changed: any[] }) => {
      // debug(viewableItems);
      // debug(changed);
      changed
        .filter((view) => view.isViewable && view.item && view.item.campaign)
        .forEach((view) => {
          setTimeout(() => {
            trackAdvertView(view.item);
          });
        });
    },
    [],
  );

  // allow the (parent) ref holder to call this function
  useImperativeHandle(ref, () => ({
    scrollToTop() {
      flatListRef.current?.scrollToOffset({ animated: false, offset: 0 });
    },
  }));

  const advertCategoriesCheck = () => {
    return !lodash.isEmpty(advertCategoryList);
  };

  const thumbtackProsCheck = () => {
    return !lodash.isEmpty(customListItemData);
  };
  const thumbtackCategoriesCheck = () => {
    return !lodash.isEmpty(thumbtackCategoryList);
  };

  if (
    !advertCategoriesCheck() &&
    !thumbtackProsCheck() &&
    !thumbtackCategoriesCheck()
  ) {
    return (
      <View style={styles.listItemContainer}>
        <Text style={styles.noMatches}>
          {provider === Provider.Thumbtack
            ? 'No local pros found for that service.'
            : 'Sorry, no content was matched.'}
        </Text>
        <Text style={styles.noMatches}>
          {provider === Provider.Thumbtack
            ? 'Please try a different search.'
            : 'Try removing one or more filters.'}
        </Text>
      </View>
    );
  }

  if (Platform.OS === 'web') {
    return (
      <GridList
        advertCategoryList={data}
        customListItemComponent={customListItemComponent}
        onPress={onPress}
      />
    );
  }

  return (
    <FlatList
      style={styles.list}
      contentContainerStyle={styles.list}
      data={data}
      ref={flatListRef}
      renderItem={renderItem}
      horizontal={false}
      {...flatListOptimizationProps}
      viewabilityConfig={{
        minimumViewTime: config.advertTracking.carousel.minimumViewTime,
        viewAreaCoveragePercentThreshold:
          config.advertTracking.carousel.viewAreaCoveragePercentThreshold,
      }}
      onViewableItemsChanged={handleViewableItemsChanged}
    />
  );
});

const styles = StyleSheet.create({
  list: {
    flexGrow: 1,
    paddingBottom: config.theme.list.marginBottom,
    // maxWidth: 1200,
  },
  listItemContainer: {
    marginHorizontal: 15,
  },
  divider: {
    marginTop: 0,
  },
  featuredHeadingContainer: {
    backgroundColor: config.theme.brandColor,
    paddingStart: 18,
    paddingEnd: 31.5,
    // previous paddingVertical: 7.5
    // paddingVertical: 5,
    position: 'absolute',
    right: 0,
    // previous top: 28
    // top: 1,
  },
  featuredHeadingContainerZonetail: {
    paddingVertical: 7.5,
    top: 28,
  },
  featuredHeadingContainerThumbtack: {
    paddingVertical: 5,
    top: 1,
  },
  featuredHeading: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '700',
  },
  noMatches: {
    color: config.theme.headingTextColor,
    fontSize: 14,
    fontWeight: 'normal',
    textAlign: 'center',
  },
  flexOne: {
    flex: 1,
  },
});

export default CategoryAdvertList;
